import { useEffect, useState } from "react";
import { BarChart3, Clock, Download, Eye, Globe, LineChart, Repeat, TrendingUp } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import StatCard from "@/components/admin/StatCard";
import { adminApi } from "@/lib/adminApi";
import { useT } from "@/i18n/LanguageContext";

/**
 * /admin/analytics — Analytics dashboard (placeholder-driven).
 *
 * Live counters come from stats/summary where available (published articles,
 * messages, waitlist). Traffic/reading-time metrics are placeholders until
 * GA4 is proxied server-side. Layout is production-ready.
 */

const TrafficSources = [
  { name: "Direct",            pct: 42, dev: false },
  { name: "Google (organic)",  pct: 27, dev: false },
  { name: "Referral",          pct: 12, dev: false },
  { name: "Social",            pct: 11, dev: false },
  { name: "Email",             pct: 5,  dev: false },
  { name: "Other",             pct: 3,  dev: false },
];

const TopArticles = [
  { title: "How Ancient Wisdom Helped Me Through a Difficult Period", views: "—" },
  { title: "The Discipline of Assent",   views: "—" },
  { title: "On Anger: Seneca's Guide",  views: "—" },
];

export default function AdminAnalytics() {
  const { t } = useT();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.summary().then(setSummary).finally(() => setLoading(false));
  }, []);

  const a = summary?.articles || {};
  const w = summary?.waitlist || {};

  return (
    <AdminShell title={t("admin.analytics.title")}>
      <p className="text-sm text-bone/60 -mt-6 mb-8 max-w-2xl">{t("admin.analytics.intro")}</p>

      {/* Top KPIs */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="analytics-kpis">
        <StatCard testId="an-visitors"  icon={Globe}      label={t("admin.analytics.visitors")}       value="—" hint="30d · GA4 pending" accent="fog" />
        <StatCard testId="an-jviews"    icon={Eye}        label={t("admin.analytics.journalViews")}   value="—" hint="30d · GA4 pending" accent="fog" />
        <StatCard testId="an-downloads" icon={Download}   label={t("admin.analytics.downloads")}      value="—" hint={t("admin.label.placeholder")} accent="fog" />
        <StatCard testId="an-conv"      icon={TrendingUp} label={t("admin.analytics.conversion")}     value="—" hint={t("admin.analytics.conversion.hint")} accent="fog" />
        <StatCard testId="an-published" icon={BarChart3}  label={t("admin.analytics.articlesLive")}   value={a.published ?? "—"} hint={`${a.total ?? "—"} ${t("admin.label.total").toLowerCase()}`} loading={loading} />
        <StatCard testId="an-time"      icon={Clock}      label={t("admin.analytics.readingTime")}    value="—" hint={t("admin.label.placeholder")} accent="fog" />
        <StatCard testId="an-return"    icon={Repeat}     label={t("admin.analytics.returnVisitors")} value="—" hint={t("admin.label.placeholder")} accent="fog" />
        <StatCard testId="an-waitlist"  icon={LineChart}  label={t("admin.analytics.waitlist")}       value={w.total ?? "—"} hint={t("admin.analytics.waitlist.hint")} loading={loading} />
      </section>

      {/* Two column: Traffic + Top articles */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
        <div>
          <h2 className="font-serif text-xl text-bone mb-4">{t("admin.analytics.traffic")}</h2>
          <div className="border border-gold/15 bg-white/[0.02] p-5 md:p-6 space-y-4" data-testid="traffic-sources">
            {TrafficSources.map((s) => (
              <div key={s.name}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[13px] text-bone/85">{s.name}</span>
                  <span className="text-[11px] text-bone/50">{s.pct}%</span>
                </div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-gold/60" style={{ width: `${s.pct}%` }} />
                </div>
              </div>
            ))}
            <p className="pt-3 text-[10px] tracking-[0.28em] uppercase text-bone/40 border-t border-white/5">
              {t("admin.analytics.trafficNote")}
            </p>
          </div>
        </div>

        <div>
          <h2 className="font-serif text-xl text-bone mb-4">{t("admin.analytics.topArticles")}</h2>
          <div className="border border-gold/15 bg-white/[0.02]" data-testid="top-articles">
            {TopArticles.map((art, i) => (
              <div key={i} className="flex items-center justify-between gap-4 px-5 py-3 border-b border-white/5 last:border-b-0">
                <div className="min-w-0">
                  <span className="text-[10px] tracking-[0.24em] uppercase text-gold/60 mr-3">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[13px] text-bone/85 truncate">{art.title}</span>
                </div>
                <span className="text-[11px] text-bone/50 flex-shrink-0">{art.views}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </AdminShell>
  );
}
